/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */

'use client';

import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import dynamic from 'next/dynamic';
import { useSearchParams, useRouter } from 'next/navigation';
import { UserData } from 'stores/AuthStore';
import useAuth from 'hooks/useAuth';
import { Spinner } from '@sympla/sympla-components';
import { addEventToDataLayer } from 'utils/addEventToDataLayer';

import { getInitials } from 'utils/getInitials';
import { getCookie } from 'cookies-next';
import { waitForSymplaAuthLogout } from 'utils/waitForSymplaAuth';
import { setAuthenticatedUserTokenAlgolia } from 'utils/algolia';
import * as styles from './Session.css';
import NewUserSpace from './UserSpace/UserSpace';

const Session = dynamic(() => import('./Session'), { ssr: false });
const ModalAuthenticated = dynamic(() => import('./MenuMobileAuthenticated/MenuMobileAuthenticated'), { ssr: false });
const env: string | false = process?.env?.NEXT_PUBLIC_CLOUD_FRONT_BUNDLE_ENVIRONMENT || false;

function Authentication({ isAuthenticated, isNavbarCollapsed = false }: { isAuthenticated: boolean, isNavbarCollapsed?: boolean}) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const searchParams = useSearchParams();
  const router = useRouter();
  const authStore = useAuth();

  async function handleLogout() {
    try {
      const isLogoutReady = await waitForSymplaAuthLogout(3000, 10, 200);
      if (isLogoutReady) {
        authStore.logOut();
        window.symplaAuthComponent.logout();
      } else {
        authStore.logOut();
      }
    } catch (error) {
      authStore.logOut();
    }

    setIsDropdownOpen(false);
  }

  useEffect(() => {
    const logoutParam = searchParams?.get('logout');
    if (logoutParam === 'true') {
      try {
        handleLogout();

        const url = new URL(window.location.href);
        url.searchParams.delete('logout');
        router.replace(url.pathname + url.search, { scroll: false });
      } catch (error) {
        // Erro ao processar logout via query parameter
      }
    }
  }, [searchParams, authStore, router]);

  useEffect(() => {
    authStore.isAuthenticated = isAuthenticated;

    // Delay para garantir que o cookie está disponível
    const timeout = setTimeout(() => {
      const redisSessionCookie = getCookie(`_symplaSession-${env}`);
      if (redisSessionCookie) {
        authStore.getSession(redisSessionCookie);
      }
      authStore.setIsRecoveringSession(false);
    }, 100);

    return () => clearTimeout(timeout);
  }, [isAuthenticated]);

  useEffect(() => {
    if (authStore.isAuthenticated) setAuthenticatedUserTokenAlgolia();
  }, [authStore.isAuthenticated]);

  async function onHandleAuth(userData: UserData) {
    const checkSession = document.querySelector('auth-component #keepMeConnected') as HTMLInputElement;
    const shouldKeepSession = checkSession?.checked;
    authStore.updateUserData(userData, shouldKeepSession);
  }

  if (authStore.isRecoveringSession) {
    return <Spinner className={styles.LoadingContainer} />;
  }

  const handleTrankingUserSpace = (label: string) => {
    addEventToDataLayer({
      stckEventCategory: 'home_clique_header',
      stckEventLabel: `Perfil-${label}`,
    });
  };

  return (
    <div className={`${styles.container} animated fadeIn`} data-testid="header-auth-container">
      <NewUserSpace
        isNavbarCollapsed={isNavbarCollapsed}
        handleLogout={handleLogout}
        isDropdownOpen={isDropdownOpen}
        setIsDropdownOpen={(value: boolean) => setIsDropdownOpen(value)}
        handleTrankingUserSpace={handleTrankingUserSpace}
      />
      <Session
        activeComponent={authStore.modalType}
        modalOpen={authStore.isModalOpen}
        forgotPsw={authStore.openForgotPswModal}
        hideModal={authStore.onCloseModal}
        lang="pt"
        logIn={onHandleAuth}
        signUp={onHandleAuth}
      />
      {authStore?.userData?.token && (
        <ModalAuthenticated
          name={authStore.userData.dataUser.fullName}
          nameInitials={getInitials(authStore.userData.dataUser.fullName)}
          email={authStore.userData.dataUser.email}
          isModalAuthenticatedVisible={authStore.isModalAuthenticatedVisible}
          closeModalAuthenticated={() => authStore.setModalAuthenticatedVisible(false)}
          handleLogout={() => handleLogout()}
          handleTrankingUserSpace={handleTrankingUserSpace}
        />
      )}

    </div>
  );
}

export default observer(Authentication);
